/**
 * Contrato del share en vivo (docs/SHARE-SCREEN-SPEC.md + decisiones del 2026-09-03).
 * Estos tipos son el espejo EXACTO de lo que devuelve /api/share/*: si el backend cambia un
 * campo se cambia acá y el compilador señala a quién le duele.
 */

import type { RecordingDTO } from '../rec/types';

export type ShareStatus =
  | 'pending' | 'accepted' | 'live' | 'ended' | 'declined' | 'expired' | 'cancelled' | 'failed';

export type EndReason =
  | 'sharer_stopped' | 'viewer_left' | 'sharer_left' | 'ice_failed' | 'session_cut'
  | 'declined' | 'expired' | 'cancelled' | 'timeout' | 'busy';

export type CandidatePair = 'host' | 'srflx' | 'prflx' | 'relay';

/**
 * Estado de un participante (una fila de `screen_share_participants`, un viewer de un broadcast).
 * `left`/`declined`/`expired`/`removed`/`failed` son terminales para ESE viewer; el broadcast puede
 * seguir vivo con otros (o con cero, esperando a que el sharer agregue más).
 */
export type ParticipantStatus =
  | 'pending' | 'accepted' | 'live' | 'left' | 'declined' | 'expired' | 'failed' | 'removed';

/**
 * El BROADCAST del sharer (una fila de `screen_share_sessions` reinterpretada en la malla multi-
 * viewer, spec §14). Lleva la captura/audio del sharer UNA vez y el roster de participantes. El
 * sharer consume esto (por `share.broadcast` y `/state`); el viewer sigue viendo un `SessionDTO`.
 */
export type BroadcastDTO = {
  id: number;
  status: 'pending' | 'live' | 'ended' | 'cancelled' | 'failed';
  sharer: { id: string; name: string };
  sharerTabId: string;
  audioMic: boolean;
  audioSystem: boolean;
  /** El sharer silenció su micrófono: aplica a TODOS los viewers. */
  micMuted: boolean;
  surface: string | null;
  /** Techo por viewer (kbps) fijado por el sharer, o null: el reparto no lo pasa. */
  maxBitrateKbps: number | null;
  /** Presupuesto TOTAL de subida (kbps) a repartir entre los viewers. */
  uploadBudgetKbps: number;
  /** Cap de espectadores simultáneos. */
  maxViewers: number;
  requestedAt: string;
  endedAt: string | null;
  endReason: EndReason | null;
  endedBy: 'sharer' | 'viewer' | 'system' | null;
  /** Cuántos están efectivamente mirando (participantes en 'live'). */
  viewerCount: number;
  participants: ParticipantDTO[];
};

/** Una pata de entrega del broadcast: el estado por-viewer (una fila de `screen_share_participants`). */
export type ParticipantDTO = {
  /** Id del participante (pid), NO el del broadcast. */
  id: number;
  viewer: { id: string; name: string };
  viewerTabId: string | null;
  status: ParticipantStatus;
  viewerRole: string | null;
  seenAt: string | null;
  acceptedAt: string | null;
  connectedAt: string | null;
  endedAt: string | null;
  endReason: EndReason | null;
  candidatePair: CandidatePair | null;
  relayUsed: boolean | null;
  /** El viewer silenció SU micrófono (ida y vuelta): el sharer pinta "{name} muted their microphone". */
  viewerMicMuted: boolean;
  /** Cap por viewer vigente (dinámico, baja al crecer el número de espectadores). */
  maxBitrateKbps: number | null;
  poppedOut: 'doc_pip' | 'video_pip' | 'popup' | 'screen2' | null;
  requestedAt: string;
  expiresAt: string;
};

export type SessionDTO = {
  id: number;
  status: ShareStatus;
  sharer: { id: string; name: string };
  viewer: { id: string; name: string };
  sharerTabId: string;
  viewerTabId: string | null;
  /** [rev.3] Qué audio armó el sharer: voz (mic) y/o sistema. Reemplazan a `withAudio`. */
  audioMic: boolean;
  audioSystem: boolean;
  /** El sharer silenció su micrófono (`POST /:id/mute`): el viewer pinta "Muted by {name}". */
  micMuted: boolean;
  /** El VIEWER silenció el suyo (`POST /:id/mute { side: 'viewer' }`, vive en `stats_json`): el sharer pinta "{name} muted their microphone". */
  viewerMicMuted: boolean;
  /** Rol del que mira ('office'… | 'inspector'): el inspector ve pero nunca comparte. */
  viewerRole: string | null;
  requestedAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  connectedAt: string | null;
  endedAt: string | null;
  endReason: EndReason | null;
  endedBy: 'sharer' | 'viewer' | 'system' | null;
  candidatePair: CandidatePair | null;
  relayUsed: boolean | null;
  poppedOut: 'doc_pip' | 'video_pip' | 'popup' | 'screen2' | null;
  maxBitrateKbps: number | null;
  /** [malla] Cuántos MÁS están mirando el mismo broadcast (para pintar "N others watching"). Opcional. */
  viewerCount?: number;
};

export type SignalKind = 'offer' | 'answer' | 'ice' | 'ice-restart' | 'bye';

export type SignalDTO = {
  id: number;
  sessionId: number;
  fromUserId: string;
  /** [malla] Pestaña de quien envía: el sharer demultiplexa la señal al peer del viewer correcto. */
  fromTabId: string | null;
  toUserId: string;
  toTabId: string | null;
  kind: SignalKind;
  payload: Record<string, unknown>;
  createdAt: string;
};

export type TargetRow = {
  id: string;
  name: string;
  role: string;
  department: string | null;
  appOpen: boolean;
  humanRecent: boolean;
  busy: null | 'sharing' | 'viewing';
  /** Grupo del selector: mi depto, mis otros deptos, Everyone, y Field team (inspectores) al final. */
  group: 'mine' | 'other' | 'everyone' | 'field';
  /** Mira desde un teléfono: puede ver; el audio requiere un toque (autoplay). */
  phone: boolean;
  /** Inspector con la cita de hoy en `checked_in`/`in_progress`: "On a job". */
  onJob: boolean;
};

export type TargetsResponse = { rows: TargetRow[]; myDepartments: string[] };

export type StateResponse = {
  pending: SessionDTO[];
  /** Vista del VIEWER: la sesión que estoy mirando (compuesta broadcast+mi participant). */
  active: SessionDTO | null;
  /** Vista del SHARER: mi broadcast, si estoy compartiendo. */
  broadcast: BroadcastDTO | null;
  sessionCutAt: string | null;
  idleLimitMs: number;
  serverNow: string;
};

/**
 * Respuesta de `POST /invites` (crea el broadcast + primer participante, O agrega un participante a
 * mi broadcast vivo). Trae el broadcast entero, el participante recién creado, y los iceServers para
 * armar el peer hacia ESE viewer.
 */
export type InviteResponse = {
  broadcast: BroadcastDTO;
  participant: ParticipantDTO;
  iceServers: RTCIceServer[];
  maxBitrateKbps: number;
  uploadBudgetKbps: number;
  targetAppOpen: boolean;
};

export type AcceptResponse = { session: SessionDTO; iceServers: RTCIceServer[]; maxBitrateKbps: number };
export type HandoffResponse = { session: SessionDTO; iceServers: RTCIceServer[] };

/** Roster del broadcast (solo lectura): `GET /:id/participants`. */
export type ParticipantsResponse = { participants: ParticipantDTO[] };

/** Mensajes que el servidor empuja por el WebSocket de solo-lectura (/api/rt). */
export type RtMessage =
  | { t: 'hello'; hubInstance: string; serverNow: string; tabId: string }
  | { t: 'hb' }
  | { t: 'share.invite'; session: SessionDTO }
  | { t: 'share.update'; session: SessionDTO }
  | { t: 'share.signal'; signal: SignalDTO }
  | { t: 'share.ended'; session: SessionDTO }
  // [malla] Al SHARER: cada cambio de roster/estado de SU broadcast (reemplaza el share.update que el sharer consumía).
  | { t: 'share.broadcast'; broadcast: BroadcastDTO }
  // Record (spec §4.12.4): el remux terminó (o falló) → dueño y destinatarios actualizan la tarjeta.
  | { t: 'recording.ready'; recording: RecordingDTO }
  | { t: 'recording.failed'; recording: RecordingDTO };

export type Quality = 'good' | 'fair' | 'poor';

/** Lo mínimo de `PeerStats` que la malla mira para el aviso de estrés de subida (así se prueba puro). */
export type PeerStatsLike = { limitation: string | null };

export type PeerStats = {
  quality: Quality;
  rttMs: number | null;
  fps: number | null;
  bitrateKbps: number | null;
  lossFraction: number | null;
  limitation: string | null;
  pair: CandidatePair | null;
  relayUsed: boolean;
};

/** Lo que vive en la sesión viva: sigue abierta mientras haya alguien de estos estados. */
export const LIVE_STATUSES: ReadonlySet<ShareStatus> = new Set(['pending', 'accepted', 'live']);
